/**
 * Stable snapshot hashes for "materially different" personalized plans.
 * Mirrors usePersonalizedPlan's early return so hashes align with on-screen state.
 */
import { applyDeltas } from './apply-deltas'
import type { AnswerDelta, PersonalizedTemplate } from './apply-deltas'
import type { ProjectTemplate, TemplateStep } from '../types'

function serializeStep(step: TemplateStep): unknown {
  return {
    slug: step.slug,
    title: step.title,
    description: step.description,
    estimatedMinutes: step.estimatedMinutes,
    tools: step.tools,
    materials: step.materials,
    tips: step.tips,
    warnings: step.warnings,
  }
}

function serializePersonalized(p: PersonalizedTemplate): string {
  return JSON.stringify({
    hours: p.estimatedHours,
    cmin: p.estimatedCostMin,
    cmax: p.estimatedCostMax,
    difficulty: p.difficulty,
    modified: [...p.modifiedStepIndices].sort((a, b) => a - b),
    steps: p.steps.map(serializeStep),
  })
}

function baseFromTemplate(template: ProjectTemplate): PersonalizedTemplate {
  return {
    title: template.title,
    estimatedHours: template.estimatedHours ?? 0,
    estimatedCostMin: template.estimatedCostMin ?? 0,
    estimatedCostMax: template.estimatedCostMax ?? 0,
    difficulty: template.difficulty,
    steps: template.steps,
    modifiedStepIndices: new Set(),
  }
}

/**
 * Returns a stable string hash of the personalized plan for the given answers.
 * Same rules as `usePersonalizedPlan` (no deltas or empty answers → base template).
 */
export function getPersonalizedSnapshotHash(
  template: ProjectTemplate,
  answers: Record<string, string>,
  deltas: Array<AnswerDelta>,
  enabled: boolean
): string {
  if (!enabled || deltas.length === 0 || Object.keys(answers).length === 0) {
    return serializePersonalized(baseFromTemplate(template))
  }
  const p = applyDeltas(template, answers, deltas)
  return serializePersonalized(p)
}
